import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const PhoneTracker = () => {
  const mapRef = useRef(null);
  const [userLocation, setUserLocation] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [callerImei, setCallerImei] = useState('');
  const [receiverImei, setReceiverImei] = useState('');
  const [callerLocation, setCallerLocation] = useState(null);
  const [receiverLocation, setReceiverLocation] = useState(null);
  const [marker, setMarker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // ✅ Use your new API key
  const GOOGLE_MAPS_API_KEY = 'AIzaSyC73e0BfAVaLYbptzEWEybxZEW9yZyim3Y';

  // ========== IMPROVED LOCATION useEffect ==========
  useEffect(() => {
    if (navigator.geolocation) {
      // Set a timeout for geolocation request
      const timeoutId = setTimeout(() => {
        setUserLocation({ lat: 6.5244, lng: 3.3792 }); // Fallback to Lagos
        console.log('📍 Using fallback location (Lagos) due to timeout');
      }, 5000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          console.log('📍 Got user location successfully');
        },
        (error) => {
          clearTimeout(timeoutId);
          console.error('Error fetching location:', error.code, error.message);
          
          // Handle different error codes
          switch(error.code) {
            case 1: // PERMISSION_DENIED
              setErrorMessage('📍 Location permission denied. Using default location (Lagos).');
              break;
            case 2: // POSITION_UNAVAILABLE
              setErrorMessage('📍 Location unavailable. Using default location (Lagos).');
              break;
            case 3: // TIMEOUT
              setErrorMessage('📍 Location request timed out. Using default location (Lagos).');
              break;
            default:
              setErrorMessage('📍 Could not get location. Using default location (Lagos).');
          }
          
          setUserLocation({ lat: 6.5244, lng: 3.3792 }); // Fallback to Lagos
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setUserLocation({ lat: 6.5244, lng: 3.3792 });
      setErrorMessage('📍 Geolocation not supported. Using default location (Lagos).');
    }
  }, []);

  // Load Google Maps script
  useEffect(() => {
    if (!userLocation || mapLoaded) return;

    const scriptId = 'google-maps-script';

    const initMap = () => {
      if (mapRef.current && window.google) {
        try {
          const map = new window.google.maps.Map(mapRef.current, {
            center: userLocation,
            zoom: 12,
            mapTypeId: 'roadmap',
            mapTypeControl: true,
            fullscreenControl: true,
            streetViewControl: true,
          });

          // Add user location marker using improved function
          showOnMap(userLocation, 'Your Location', 'blue', map);

          setMapInstance(map);
          setMapLoaded(true);
        } catch (err) {
          setLoadError('Failed to initialize map.');
          console.error('Map init error:', err);
        }
      }
    };

    const loadScript = () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&callback=initMap`;
      script.async = true;
      script.defer = true;

      script.onload = () => console.log('✅ Maps script loaded successfully');
      script.onerror = () => {
        setLoadError('❌ Failed to load Google Maps script. Check your API key.');
        console.error('Maps script load error');
      };

      document.head.appendChild(script);
    };

    window.initMap = initMap;
    loadScript();

    return () => {
      const script = document.getElementById(scriptId);
      if (script) script.remove();
      delete window.initMap;
    };
  }, [userLocation, mapLoaded]);

  // ========== IMPROVED showOnMap FUNCTION ==========
  const showOnMap = (coordinates, title = 'Phone Location', color = 'red', targetMap = mapInstance) => {
    const mapToUse = targetMap || mapInstance;
    if (!mapToUse || !window.google) return;

    const position = { lat: coordinates.lat, lng: coordinates.lng };
    mapToUse.setCenter(position);
    mapToUse.setZoom(15);
    
    // Color mapping for markers
    const colorMap = {
      red: 'F44336',
      green: '4CAF50',
      blue: '2196F3',
      yellow: 'FFC107'
    };
    
    try {
      let newMarker;
      
      // Try using AdvancedMarkerElement (modern API)
      if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
        const { AdvancedMarkerElement } = window.google.maps.marker;
        
        newMarker = new AdvancedMarkerElement({
          position: position,
          map: mapToUse,
          title: title,
        });
      } else {
        // Fallback to classic marker
        newMarker = new window.google.maps.Marker({
          position: position,
          map: mapToUse,
          title: title,
          animation: window.google.maps.Animation.DROP,
          icon: {
            url: `http://maps.google.com/mapfiles/ms/icons/${color}-dot.png`
          }
        });
      }
      
      // Add info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            <h3 style="margin: 0 0 5px 0;">📱 ${title}</h3>
            <p style="margin: 5px 0;"><strong>Lat:</strong> ${coordinates.lat.toFixed(6)}</p>
            <p style="margin: 5px 0;"><strong>Lng:</strong> ${coordinates.lng.toFixed(6)}</p>
            <p style="margin: 5px 0;"><small>${new Date().toLocaleString()}</small></p>
          </div>
        `
      });
      
      infoWindow.open(mapToUse, newMarker);
      
    } catch (error) {
      console.error('Error creating marker:', error);
    }
  };

 // ========== AUTO LOCATION REFRESH WITH SETTINGS BUTTON ==========
const refreshLocation = () => {
  setLoading(true);
  setErrorMessage(""); // Clear any previous errors
  
  if (navigator.geolocation) {
    console.log("📍 Requesting device location for tracking...");
    
    // Show a message that we're getting location
    setSuccessMessage("📍 Getting your current location...");
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Get the current device location
        const currentLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
        
        console.log("📍 Device location obtained:", currentLocation);
        
        // Update state with new location
        setUserLocation(currentLocation);
        
        // Show success message with accuracy
        setSuccessMessage(`📍 Location updated! Accuracy: ±${Math.round(currentLocation.accuracy)}m`);
        
        // Update map if it exists
        if (map) {
          map.setCenter(currentLocation);
          map.setZoom(16); // Zoom in closer for accuracy
          
          // Add or update marker with current location
          showOnMap(currentLocation, 'Your Current Location', 'blue');
          
          // Add accuracy circle if using advanced maps
          if (window.google && window.google.maps) {
            // Remove old accuracy circle if exists
            if (window.accuracyCircle) {
              window.accuracyCircle.setMap(null);
            }
            
            // Draw accuracy circle
            window.accuracyCircle = new window.google.maps.Circle({
              strokeColor: '#4285F4',
              strokeOpacity: 0.3,
              strokeWeight: 1,
              fillColor: '#4285F4',
              fillOpacity: 0.15,
              map: map,
              center: currentLocation,
              radius: currentLocation.accuracy // Use actual accuracy
            });
          }
        }
        
        // **AUTO-TRACK after getting location** 
        // If we have a phone number to track, automatically track it
        if (phoneNumber) {
          setSuccessMessage(`📍 Got your location! Now tracking ${phoneNumber}...`);
          trackPhoneByNumber(phoneNumber);
        } else if (imei) {
          setSuccessMessage(`📍 Got your location! Now tracking IMEI: ${imei}...`);
          trackPhoneByIMEI(imei);
        } else {
          setSuccessMessage(`📍 Location updated! Enter a phone number or IMEI to track.`);
        }
        
        setLoading(false);
      },
      (error) => {
        console.error('❌ Error getting device location:', error.code, error.message);
        
        // Handle specific errors with user-friendly messages and settings button
        switch(error.code) {
          case 1: // PERMISSION_DENIED
            setErrorMessage(
              <div>
                <p>❌ Location permission denied. Please enable location access:</p>
                <button 
                  onClick={openLocationSettings}
                  style={{
                    backgroundColor: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    marginTop: '10px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ⚙️ Open Location Settings
                </button>
              </div>
            );
            break;
          case 2: // POSITION_UNAVAILABLE
            setErrorMessage(
              <div>
                <p>❌ Location unavailable. Please check if GPS/location is enabled:</p>
                <button 
                  onClick={openLocationSettings}
                  style={{
                    backgroundColor: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    marginTop: '10px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ⚙️ Open Location Settings
                </button>
              </div>
            );
            break;
          case 3: // TIMEOUT
            setErrorMessage(
              <div>
                <p>❌ Location request timed out. Please try again in a better signal area:</p>
                <button 
                  onClick={refreshLocation}
                  style={{
                    backgroundColor: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    marginTop: '10px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  🔄 Try Again
                </button>
              </div>
            );
            break;
          default:
            setErrorMessage(
              <div>
                <p>❌ Failed to get location: {error.message}</p>
                <button 
                  onClick={openLocationSettings}
                  style={{
                    backgroundColor: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    marginTop: '10px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ⚙️ Open Location Settings
                </button>
              </div>
            );
        }
        
        setLoading(false);
      },
      {
        enableHighAccuracy: true, // Use GPS for best accuracy
        timeout: 15000, // Wait up to 15 seconds
        maximumAge: 0 // Always get fresh location, don't use cached
      }
    );
  } else {
    setErrorMessage(
      <div>
        <p>❌ Geolocation is not supported by your browser.</p>
        <p>Please use Chrome, Edge, Firefox, or Safari.</p>
      </div>
    );
    setLoading(false);
  }
};

// ========== OPEN LOCATION SETTINGS ==========
const openLocationSettings = () => {
  // This will open the browser's location settings or system settings
  if (navigator.permissions && navigator.permissions.query) {
    // For modern browsers, we can't directly open settings,
    // but we can show instructions
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile devices, we can try to open system settings
      if (/Android/i.test(navigator.userAgent)) {
        // Android - try to open location settings
        window.location.href = 'android.settings.LOCATION_SOURCE_SETTINGS';
        setTimeout(() => {
          setErrorMessage('If settings didn\'t open, please manually enable location in: Settings → Location');
        }, 1000);
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // iOS - try to open privacy settings
        window.location.href = 'App-Prefs:root=Privacy&path=LOCATION';
        setTimeout(() => {
          setErrorMessage('If settings didn\'t open, please manually enable location in: Settings → Privacy → Location Services');
        }, 1000);
      } else {
        // Other mobile
        setErrorMessage('Please enable location in your device settings');
      }
    } else {
      // Desktop - show browser-specific instructions
      const browser = getBrowser();
      let instructions = '';
      
      switch(browser) {
        case 'chrome':
          instructions = 'Click the lock icon in the address bar → Site Settings → Location → Allow';
          break;
        case 'edge':
          instructions = 'Click the lock icon in the address bar → Site permissions → Location → Allow';
          break;
        case 'firefox':
          instructions = 'Click the lock icon in the address bar → Connection secure → More information → Permissions → Access Your Location → Allow';
          break;
        case 'safari':
          instructions = 'Safari → Preferences → Websites → Location → Allow';
          break;
        default:
          instructions = 'Please enable location in your browser settings';
      }
      
      setErrorMessage(`📋 ${instructions}`);
    }
  }
};

// ========== HELPER TO DETECT BROWSER ==========
const getBrowser = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf("Chrome") > -1) return 'chrome';
  if (userAgent.indexOf("Safari") > -1) return 'safari';
  if (userAgent.indexOf("Firefox") > -1) return 'firefox';
  if (userAgent.indexOf("Edg") > -1) return 'edge';
  return 'unknown';
};

  // Function to show both caller and receiver on map
  const showLocationsOnMap = (caller, receiver) => {
    if (!mapInstance || !window.google) return;

    // Add caller marker
    if (caller) {
      showOnMap(
        { lat: caller.latitude, lng: caller.longitude },
        'Caller',
        'green',
        mapInstance
      );
    }

    // Add receiver marker
    if (receiver) {
      showOnMap(
        { lat: receiver.latitude, lng: receiver.longitude },
        'Receiver',
        'red',
        mapInstance
      );
    }

    // Fit bounds to show all markers
    const bounds = new window.google.maps.LatLngBounds();
    if (caller) bounds.extend({ lat: caller.latitude, lng: caller.longitude });
    if (receiver) bounds.extend({ lat: receiver.latitude, lng: receiver.longitude });
    if (userLocation) bounds.extend(userLocation);
    mapInstance.fitBounds(bounds);
  };

  // Handle track request
  const handleTrack = async () => {
    if (!callerImei && !receiverImei) {
      alert('Please enter at least one IMEI');
      return;
    }

    setIsScanning(true);

    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/phones/call-location', {
        callerImei: callerImei || undefined,
        receiverImei: receiverImei || undefined,
      });

      const { callerLocation, receiverLocation } = response.data;
      
      setCallerLocation(callerLocation);
      setReceiverLocation(receiverLocation);
      
      if (mapInstance) {
        showLocationsOnMap(callerLocation, receiverLocation);
      }
      setSuccessMessage('✅ Call locations retrieved successfully');
    } catch (err) {
      console.error('Tracking error:', err);
      setErrorMessage('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsScanning(false);
    }
  };

  if (loadError) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <h3>⚠️ Map Error</h3>
        <p>{loadError}</p>
        <p>Make sure you've enabled the Maps JavaScript API in Google Cloud Console and enabled billing.</p>
      </div>
    );
  }

  if (!userLocation) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>📍 Getting your location...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>📱 Phone Tracker</h1>

      {/* Input Section */}
      <div style={styles.inputSection}>
        <input
          type="text"
          placeholder="Caller IMEI"
          value={callerImei}
          onChange={(e) => setCallerImei(e.target.value)}
          style={styles.input}
        />
        <input
          type="text"
          placeholder="Receiver IMEI"
          value={receiverImei}
          onChange={(e) => setReceiverImei(e.target.value)}
          style={styles.input}
        />
        <button 
          onClick={handleTrack} 
          disabled={isScanning}
          style={styles.button}
        >
          {isScanning ? '🔄 Scanning...' : '🎯 Track Call'}
        </button>
      </div>

      {/* Refresh Location Button */}
      <div style={styles.locationHeader}>
        <button
          onClick={refreshLocation}
          disabled={loading}
          style={{...styles.button, backgroundColor: '#ff9800', marginBottom: '15px'}}
        >
          {loading ? '📍 Refreshing...' : '📍 Refresh My Location'}
        </button>
      </div>

      {/* Radar Animation */}
      {isScanning && (
        <div style={styles.scannerOverlay}>
          <div style={styles.radar}></div>
          <p style={styles.scannerText}>Scanning network for devices...</p>
        </div>
      )}

      {/* Map Container */}
      <div
        ref={mapRef}
        style={styles.map}
      ></div>

      {/* Location Info */}
      {(callerLocation || receiverLocation) && (
        <div style={styles.infoPanel}>
          <h3>📍 Locations Found</h3>
          {callerLocation && (
            <div style={styles.infoItem}>
              <span style={{color: '#28a745'}}>📞 Caller:</span> 
              {callerLocation.latitude.toFixed(6)}, {callerLocation.longitude.toFixed(6)}
            </div>
          )}
          {receiverLocation && (
            <div style={styles.infoItem}>
              <span style={{color: '#dc3545'}}>📱 Receiver:</span> 
              {receiverLocation.latitude.toFixed(6)}, {receiverLocation.longitude.toFixed(6)}
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div style={styles.success}>
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div style={styles.error}>
          <strong>Error:</strong> {errorMessage}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    position: 'relative'
  },
  title: {
    textAlign: 'center',
    color: '#333',
    marginBottom: '30px'
  },
  inputSection: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    flexWrap: 'wrap'
  },
  input: {
    flex: '1',
    minWidth: '200px',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  },
  button: {
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s'
  },
  locationHeader: {
    marginBottom: '10px'
  },
  map: {
    width: '100%',
    height: '500px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  },
  scannerOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.95)',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    zIndex: 1000
  },
  radar: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    border: '5px solid #007bff',
    borderTopColor: 'transparent',
    animation: 'spin 1s infinite linear'
  },
  scannerText: {
    marginTop: '15px',
    color: '#007bff',
    fontWeight: 'bold',
    fontSize: '16px'
  },
  infoPanel: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  infoItem: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: 'white',
    borderRadius: '4px',
    borderLeft: '4px solid #007bff'
  },
  success: {
    color: '#155724',
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#d4edda',
    borderRadius: '4px',
    border: '1px solid #c3e6cb'
  },
  error: {
    color: '#721c24',
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#f8d7da',
    borderRadius: '4px',
    border: '1px solid #f5c6cb'
  }
};

// Add keyframes for radar animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default PhoneTracker;